import React from "react";
import { Box, Typography, Paper, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import Markdown from "react-markdown";
import rehypeKatex from "rehype-katex";
import remarkMath from "remark-math";
import "katex/dist/katex.min.css";

const MathLibScreen: React.FC = () => {
    const symbols = [
        { symbol: "\\pi", name: "Pi", value: "3.14159..." },
        { symbol: "e", name: "Euler's Number", value: "2.71828..." },
        { symbol: "\\phi", name: "Golden Ratio", value: "1.61803..." },
        { symbol: "\\gamma", name: "Euler-Mascheroni Constant", value: "0.57721..." },
        { symbol: "\\infty", name: "Infinity", value: "N/A" },
        { symbol: "i", name: "Imaginary Unit", value: "\\sqrt{-1}" }
    ];

    const operations = [
        { op: "\\sum_{i=1}^n", name: "Summation" },
        { op: "\\prod_{i=1}^n", name: "Product" },
        { op: "\\int", name: "Integral" },
        { op: "\\lim_{x \\to a}", name: "Limit" },
        { op: "\\sqrt[n]{x}", name: "N-th Root" },
        { op: "\\nabla", name: "Del / Nabla" }
    ];

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: "bold", color: "#2E7D32" }}>
                Mathematical Reference Library
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: "text.secondary" }}>
                A collection of common mathematical constants, symbols, and operations for reference.
            </Typography>

            <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom color="success.main">Constants</Typography>
                    <TableContainer component={Paper} elevation={2}>
                        <Table size="small">
                            <TableHead sx={{ bgcolor: 'success.light' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Symbol</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Value</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {symbols.map((row) => (
                                    <TableRow key={row.name} hover>
                                        <TableCell>
                                            <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>{`$${row.symbol}$`}</Markdown>
                                        </TableCell>
                                        <TableCell>{row.name}</TableCell>
                                        <TableCell>
                                            {row.value.startsWith('\\') ?
                                                <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>{`$${row.value}$`}</Markdown> :
                                                row.value
                                            }
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom color="success.main">Operations</Typography>
                    <Grid container spacing={2}>
                        {operations.map((op) => (
                            <Grid item xs={6} sm={4} key={op.name}>
                                <Paper sx={{
                                    p: 2,
                                    textAlign: 'center',
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    transition: 'transform 0.2s',
                                    '&:hover': { transform: 'scale(1.05)', boxShadow: 3 }
                                }}>
                                    <Box sx={{ fontSize: '1.5rem', mb: 1 }}>
                                        <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>{`$${op.op}$`}</Markdown>
                                    </Box>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{op.name}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
            </Grid>

            <Box sx={{ mt: 5 }}>
                <Typography variant="h6" gutterBottom>Sample Question Combination</Typography>
                <Paper sx={{ p: 4, bgcolor: '#e8f5e9', borderLeft: '10px solid #2E7D32' }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Calculate the following:
                    </Typography>
                    <Box sx={{ fontSize: '1.8rem', my: 2 }}>
                        <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                            {"$$ \\sum_{n=1}^{\\infty} \\frac{1}{n^2} = \\frac{\\pi^2}{6} $$"}
                        </Markdown>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        This is the Basel problem, a famous result in mathematical analysis. It combines summation, infinity, and pi.
                    </Typography>
                </Paper>
            </Box>
        </Box>
    );
};

export default MathLibScreen;
